import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isEqual, isNil } from 'lodash-es';

import {
  checkDomain,
  showEntri,
  type EntriCloseEventDetail,
  type EntriConfig,
  type EntriSuccessEventDetail,
} from 'entrijs';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchConfiguratorConfig } from '@/store/slices/configurator-config';
import {
  clearDeployProgress,
  clearDomainConnect,
  sessionDeploy,
  sessionDomainConnectConfig,
  sessionDomainConnectSetupBegin,
  sessionDomainConnectSetupFailure,
  sessionDomainConnectSetupSuccess,
  sessionLoad,
  sessionSave,
  setDomainCheck,
  SessionStatus,
  DOMAIN_ALREADY_USED_CODE,
  type ConfigReducer,
} from '@/store/slices/session';
import { writePendingBrandSessionId } from '@/store/slices/casino-instance';
import {
  clearDomainsSuggestionsByDomain,
  clearLogosSuggestions,
  fetchDomainsSuggestionsByCountry,
  fetchDomainsSuggestionsByDomain,
  fetchLogo,
  fetchThemes,
  removeLogoBackground,
  reportLogoSelected,
  uploadLogo,
} from '@/store/slices/ml-services';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { CUST_LOGO_SEED_FALLBACK, CUST_MOBILE } from '../../../theme/customizer-tokens';
// Region list + currency helpers are LOCAL to this page (ported from the Deploy
// module) — deliberately not shared with the configurator-v3 area.
import { buildRegions } from '../data/regions';
import { currencySymbol } from '../lib/currency';
import { handoffRegion, regionForLocale } from '@/nova/config/localeRegion';
// The design catalog (theme → seedId/skinId, art styles) is the configurator-v3
// area's source of truth; reused here ONLY to re-derive the design keys for the
// pre-deploy self-heal below (the deploy endpoint requires them).
import { transformCatalog, availableStyles, type Raw } from '../../configurator-v3/data/api';
import { circularSlice, nextSliceStart } from '../lib/circular';
import { useSuggestionPager, type SuggestionNav } from '../lib/useSuggestionPager';
import { normalizeDnsRecords, type DnsRecordView } from '../lib/dns';
import {
  findExtraElement,
  getCircularSlice,
  getValuesFromTokens,
  resolveSiteContext,
  type LogoSuggestion,
} from '../lib/logos';
import type { Country, CountryGroup, DomainSuggestion, NameSuggestion } from '../lib/types';
import { getDomainBlockReason } from '../lib/domain-policy';

/** Base path the wizard is mounted under (see router.tsx). Named `/account/onboarding`
 *  to disambiguate it from the pre-payment `/setup` onboarding wizard:
 *  this is the POST-payment "take your website live" flow. */
export const CUSTOMIZER_BASE = '/account/onboarding';

export type StepKey = 'country' | 'brand' | 'logo' | 'deploy';
/** Mobile-only sub-step within step 2 (also the URL hash slug).
 *  AI subflow:     'name' -> 'domain'.
 *  Manual subflow: 'own-name' -> 'own-domain' (domain screen shared with AI). */
/** Inline connect-domain error shown on the input screen (null = none). */
export type ConnectError = 'already-used' | 'blocked' | null;

export type Step2Sub = 'name' | 'domain' | 'own-name' | 'own-domain';

export interface WizardStep {
  key: StepKey;
  /** Short name used in the progress chrome + "Continue to …" CTA. */
  name: string;
  path: string;
}

export const WIZARD_STEPS: WizardStep[] = [
  { key: 'country', name: 'Operating Area', path: `${CUSTOMIZER_BASE}/country` },
  { key: 'brand', name: 'Website Name', path: `${CUSTOMIZER_BASE}/brand` },
  { key: 'logo', name: 'Logo', path: `${CUSTOMIZER_BASE}/logo` },
  { key: 'deploy', name: 'Go live', path: `${CUSTOMIZER_BASE}/deploy` },
];

/** Default region: International (USD) — matches the configurator default. */
const DEFAULT_COUNTRY = 'INT';

/** Suggestions shown at once; "Refresh suggestions" pages through by this many. */
const PAGE_SIZE = 3;

/** Domains fetched per godaddy `suggest` query (one bulk batch). "Refresh
 *  suggestions" then pages LOCALLY through this batch (PAGE_SIZE at a time) for
 *  variety, instead of re-hitting the API and risking the same top results. */
const DOMAIN_FETCH_LIMIT = 45;

/** Normalize a typed domain: drop scheme/path, trim, lower-case. */
const normalizeDomain = (value: string): string =>
  value
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/.*$/, '')
    .toLowerCase();

/**
 * Keep only http(s) URLs; any other scheme (javascript:/data:/blob:) resolves to undefined.
 * The backend-set previewUrl is fed to an <iframe src> and window.open, so a non-http(s)
 * value would run script in the studio origin — reject it before it ever reaches a sink.
 */
const safeHttpUrl = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
};

/** Logo upload limits (mirrors the Deploy module's useLogoUpload). */
const MAX_LOGO_SIZE = 12 * 1024 * 1024; // 12 MiB
const ALLOWED_LOGO_TYPES = ['jpeg', 'jpg', 'png', 'svg'];

/** How many times we auto-retry the logo-theme fetch before giving up (a failed
 *  themes load otherwise strands the whole AI-logo step). "Refresh" resets this. */
const MAX_THEME_FETCH_ATTEMPTS = 3;

/** How many extra "top-up" rounds we run to refill a batch back to `logosCount`
 *  after the dedup drops collided logos, before accepting fewer than a full grid. */
const MAX_LOGO_TOPUP_ROUNDS = 4;

type Status = 'loading' | 'ready' | 'error';

/** Manual-upload failure kind (the step maps each to localized copy). */
export type LogoError = 'unsupported' | 'failed' | null;

interface CustomizerContextValue {
  status: Status;
  isMobile: boolean;
  /** All selectable countries (flattened). */
  countries: Country[];
  /** Countries grouped by region (for the dropdown). */
  countryGroups: CountryGroup[];
  /** Currently selected country/region, or null before the catalogue resolves. */
  country: Country | null;
  /** Active currency code (from the selected country). */
  currency: string;
  /** Display symbol for the active currency. */
  currencySymbol: string;
  /** Select a country/region (switches currency + persists to the session). */
  setCountry: (code: string) => void;

  /** 1-based index of the active wizard step. */
  step: number;
  stepKey: StepKey;
  totalSteps: number;
  steps: WizardStep[];
  /** Canonical MAIN-step next-button copy — shared by the desktop `WizardFooter`
   *  and the mobile chrome so a step's primary CTA reads identically on both
   *  (mobile sub-step CTAs, e.g. 1a→1b, are handled by the chrome itself). */
  mainNextLabel: string;
  mainNextPlaceholders?: Record<string, string>;
  /** Advance to the next step / sub-step (or dismiss the mobile intro first). */
  goNext: () => void;
  /** Retreat to the previous step / sub-step (or re-open the mobile intro). */
  goBack: () => void;
  /** Whether the active step/sub-step has enough selected to continue. */
  canAdvance: boolean;

  /** Mobile-only: the intro screen ("Take your website live") is showing. */
  displayIntro: boolean;
  /** Dismiss the mobile intro → reveal step 1. */
  dismissIntro: () => void;

  // ── Step 2 — AI name & domain suggestions ─────────────────────────────────
  /** Mobile sub-step within step 2 (1a = name, 1b = domain). */
  step2Sub: Step2Sub;
  /** Windowed (3) AI name suggestions for the active region (each carries its domains). */
  names: NameSuggestion[];
  /** True while the name suggestions are being fetched (drives the skeleton). */
  loadingNames: boolean;
  /** Windowed (3) domains of the selected name (shown once a name is picked). */
  domains: DomainSuggestion[];
  selectedName: string | null;
  selectedDomain: string | null;
  setName: (name: string) => void;
  setDomain: (domain: string) => void;
  /** Page to the next 3 names (resets the section's name + domain selection). */
  refreshNames: () => void;
  /** Page to the next 3 domains of the selected name (resets the domain choice). */
  refreshDomains: () => void;
  /** True during the ~0.4s skeleton flash after paging the domain suggestions,
   *  so it's clear the list swapped (OR this into the domain skeleton gate). */
  domainsPaging: boolean;
  /** Back / forward navigation across the domain-suggestion pages the user has
   *  revealed by refreshing (the arrows beneath the domain list). */
  domainsNav: SuggestionNav;

  // ── Step 2 — manual subflow ("I have my own name") ────────────────────────
  /** True when the user is typing their own name instead of using AI suggestions. */
  manualMode: boolean;
  /** Toggle the manual subflow on/off (resets the domain choice + mobile sub-step). */
  setManualMode: (on: boolean) => void;
  /** The brand name typed by the user (manual subflow). */
  typedName: string;
  /** Update the typed name (debounced domain lookup fires automatically). */
  setTypedName: (name: string) => void;
  /** Domains suggested for the typed name (godaddy `suggest` API). */
  manualDomains: DomainSuggestion[];
  /** True while the typed-name domains are being fetched (drives the skeleton). */
  loadingManualDomains: boolean;

  // ── Step 2 — connect-domain sub-flow ("Link your own domain") ─────────────
  /** Which connect screen is showing: input → 'input', DNS records → 'dns',
   *  not in the sub-flow → null. Derived from the URL hash (#connect / #connect-dns). */
  connectStage: 'input' | 'dns' | null;
  /** Enter the connect sub-flow (from the "Connect domain" row). */
  openConnectDomain: () => void;
  /** The domain the user typed to link. */
  connectDomain: string;
  setConnectDomain: (value: string) => void;
  /** True once the typed value looks like a domain (enables submit). */
  connectDomainValid: boolean;
  /** Submit the typed domain → fetch its DNS config → advance to the records screen. */
  submitConnectDomain: () => void;
  /** Re-open the input screen (the pencil on the chosen-domain field). */
  editConnectDomain: () => void;
  /** True while the DNS config is being fetched ("Checking the available options…"). */
  connectLoading: boolean;
  /** Set when the submitted domain can't be linked: `'already-used'` means it is
   *  already connected to another instance (ERR_1003). The input screen shows a
   *  targeted message instead of advancing to the (misleading) manual-setup screen.
   *  `'blocked'` means the typed domain fails the front-end policy (government,
   *  education or a major brand); submit stays disabled while it is set. */
  connectError: ConnectError;
  /** DNS records the owner must add at their registrar (from the domain-connect config). */
  dnsRecords: DnsRecordView[];
  /** Detected registrar (e.g. "GoDaddy"), or null when unknown / manual-only. */
  connectProvider: string | null;
  /** True when the registrar supports automatic setup (Domain Connect). */
  connectAutoSupported: boolean;
  /** Open the entrijs Domain Connect modal for the chosen domain (auto-setup path). */
  openEntri: () => void;
  /** True while the entrijs Domain Connect modal is open. */
  entriOpen: boolean;

  // ── Step 3 — logo ─────────────────────────────────────────────────────────
  /** True = AI-generated logos; false = upload your own. (A toggle, not navigation.) */
  aiLogo: boolean;
  setAiLogo: (on: boolean) => void;
  /** Generated logo suggestions (streamed in over the websocket via mlServices). */
  logos: LogoSuggestion[];
  /** True while a batch of logos is still arriving (drives the skeletons). */
  loadingLogos: boolean;
  /** How many logos a batch yields (skeleton count). */
  logosCount: number;
  /** Chosen logo — a cdn_url (AI) or a data-URL (upload); persisted to the session. */
  selectedLogo: string | null;
  /** Pick a generated logo (clears any upload). */
  selectLogo: (cdnUrl: string) => void;
  /** Discard the current batch and request a fresh one. */
  refreshLogos: () => void;
  /** The uploaded file (manual mode), or null. */
  uploadedFile: File | null;
  /** `<input type="file">` change handler (gallery upload) — uploads the file and
   *  removes its background, then sets it as the selected logo. */
  onSelectLogoFile: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** True while an uploaded logo is being sent + background-removed (a synchronous
   *  ~2s job) — drives the whole-screen processing overlay. */
  logoProcessing: boolean;
  /** Last manual-upload failure (mapped to copy by the step), or null. */
  logoError: LogoError;
  /** Clear the uploaded logo. */
  resetUploadedLogo: () => void;

  // ── Step 4 — deploy / "Go live" ───────────────────────────────────────────
  /** The configurator preview URL (the casino rendered in the device mockups). */
  previewUrl: string | undefined;
  /** Configurator theme tokens (CSS custom props) applied to the preview screens. */
  previewTokens: Record<string, string> | undefined;
  /** Deploy progress from the session (websocket-driven): steps, progress %, domain. */
  deploy: ConfigReducer['deploy'];
  /** Resolved brand name for the review summary (AI or manual, falling back to the saved session). */
  brandName: string | null;
  /** Which deploy screen: review (pre-launch) → deploying → done. */
  deployPhase: 'review' | 'deploying' | 'done';
  /** Kick off the deploy (POST /deploy); progress then streams over the websocket. */
  startDeploy: () => void;
}

const CustomizerContext = createContext<CustomizerContextValue | null>(null);

export function CustomizerProvider({ children }: { children: ReactNode }): React.ReactElement {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(CUST_MOBILE);

  // Live config catalogue + authenticated session from the app's Redux store.
  const configState = useAppSelector((s) => s.configuratorConfig, isEqual);
  const session = useAppSelector((s) => s.session, isEqual);
  // AI name+domain suggestions (ml-services) — each suggestion carries its domains.
  const suggestionsRaw = useAppSelector((s) => s.mlServices.domainsSuggestionsByCountry, isEqual);
  const loadingNames = useAppSelector((s) => Boolean(s.mlServices.loadingDomainsByCountry));
  const suggestions = suggestionsRaw as NameSuggestion[];
  // Manual subflow: domains suggested for the user's typed name (godaddy API).
  const manualDomainsRaw = useAppSelector((s) => s.mlServices.domainsSuggestionsByDomain, isEqual);
  const loadingManualDomains = useAppSelector((s) => Boolean(s.mlServices.loadingDomainsByDomain));
  const manualDomains = manualDomainsRaw as DomainSuggestion[];
  // Step 3 — logo generation (themes + streamed-in logos live in mlServices; the
  // websocket already populates `logosSuggestions`, so we read it directly).
  const logosRaw = useAppSelector((s) => s.mlServices.logosSuggestions, isEqual);
  const loadingLogos = useAppSelector((s) => Boolean(s.mlServices.loadingLogos));
  const logosCount = useAppSelector((s) => s.mlServices.logosSuggestionsCount);
  const logoThemes = useAppSelector((s) => s.mlServices.logoThemes, isEqual);
  const logoOptions = useAppSelector((s) => s.mlServices.logoOptions, isEqual);
  const loadingThemes = useAppSelector((s) => Boolean(s.mlServices.loadingThemes));

  const [status, setStatus] = useState<Status>('loading');
  const [countryCode, setCountryCode] = useState<string>(DEFAULT_COUNTRY);
  // The site's look in the names spark-ai understands (art style / seed / skin /
  // market): ranks the theme list and briefs the logo planner. The market is the
  // wizard's own resolved region (saved choice, else hand-off cue, and the owner can
  // still change it in step 1), not the configurator's possibly stale `countryCode`.
  const siteContext = useMemo(
    () => resolveSiteContext(configState.config, { ...session.data.configuration_data, countryCode }),
    [configState.config, session.data.configuration_data, countryCode],
  );
  const [displayIntro, setDisplayIntro] = useState<boolean>(isMobile);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  // Name/domain provenance flags (→ persisted as `own_name` / `own_domain`).
  // These are hash-INDEPENDENT (the connect sub-flow drops `manualMode` to false,
  // so deriving them from the hash would wipe a manually-typed name on entering
  // connect): own_name = the user typed their own brand name; own_domain = the
  // user submitted their own domain via connect. Both false for AI/generated picks.
  const [ownName, setOwnName] = useState(false);
  const [ownDomain, setOwnDomain] = useState(false);
  // Client-side pagination windows: 3 NAMES and 3 DOMAINS at a time. Names come
  // from the region batch; domains from a godaddy `suggest` batch (DOMAIN_FETCH_LIMIT
  // per query). "Refresh suggestions" pages locally through each batch.
  const [nameStart, setNameStart] = useState(0);
  // Domains get the richer pager: a skeleton flash on every page change plus a
  // revealed-pages history that backs the back/forward arrows beneath the list.
  const domainPager = useSuggestionPager(manualDomains.length, PAGE_SIZE);
  const { reset: resetDomainPager, next: nextDomainPage, start: domainStart } = domainPager;
  const domainsPaging = domainPager.flashing;
  const domainsNav = useMemo<SuggestionNav>(
    () => ({
      index: domainPager.index,
      count: domainPager.count,
      canBack: domainPager.canBack,
      canForward: domainPager.canForward,
      back: domainPager.back,
      forward: domainPager.forward,
    }),
    [
      domainPager.index,
      domainPager.count,
      domainPager.canBack,
      domainPager.canForward,
      domainPager.back,
      domainPager.forward,
    ],
  );
  // Manual subflow: the typed brand name (the mode itself lives in the URL hash).
  const [typedName, setTypedNameState] = useState('');
  // Connect-domain subflow: the typed custom domain, the in-flight fetch flag, the
  // detected registrar (from entrijs checkDomain), and the "set up later" gate.
  const [connectDomain, setConnectDomainState] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [check, setCheck] = useState<{ provider: string | null; auto: boolean } | null>(null);
  const [entriOpen, setEntriOpen] = useState(false);
  // True once entri reports a successful domain connection — gates "Continue to Logo".
  const [entriSuccess, setEntriSuccess] = useState(false);
  // Step 3 — logo: the chosen logo + uploaded file + the upload-vs-generated
  // provenance flag (persisted as own_logo, seeded from the session so restore
  // shows the saved mode). The displayed mode is hash-driven with a fallback to
  // this flag (derived below), mirroring own_name for step 2.
  const [ownLogo, setOwnLogo] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // Manual upload: in-flight (upload + remove-background) flag + last failure.
  const [logoProcessing, setLogoProcessing] = useState(false);
  const [logoError, setLogoError] = useState<LogoError>(null);

  // Bootstrap: fetch the catalogue + RESUME the (paid) session once. Never mints —
  // `sessionLoad()` is non-minting, so this only ever loads an existing session.
  useEffect(() => {
    // Start every onboarding from a clean deploy slice. `clearSessionData` (fired on
    // the previous brand's deploy + by the brand switcher's "New brand") deliberately
    // leaves `deploy` intact so the just-deployed brand's "live" screen survives the
    // session teardown — but that means `deploy.loaded` LEAKS into the next brand,
    // making this step render the "done" phase on entry and never deploy. Reset once
    // here, on fresh entry: this provider mounts once per flow (step navigation keeps
    // it mounted) and the WS re-populates `deploy` during THIS brand's own deploy,
    // so the legitimate done screen is unaffected.
    dispatch(clearDeployProgress());
    if (!configState.config && !configState.loading) dispatch(fetchConfiguratorConfig());
    if (!session.loaded && !session.loading) dispatch(sessionLoad());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Post-payment guard: this wizard requires an in-progress (paid) session to
  // resume and deliberately never mints one. If the session settles with nothing to
  // resume (e.g. localStorage was cleared then this URL opened directly), the owner
  // doesn't belong here — bounce to the root guard, which routes them correctly.
  // (No loop: with no session, the root guard never routes back to onboarding.)
  useEffect(() => {
    if (session.loaded && !session.loading && !session.sessionId) {
      navigate('/', { replace: true });
    }
  }, [session.loaded, session.loading, session.sessionId, navigate]);

  useEffect(() => {
    if (configState.error) setStatus('error');
  }, [configState.error]);

  // Countries + active currency, derived from the live config (Deploy-style).
  const regions = useMemo(
    () => buildRegions(configState.config, i18n.language),
    [configState.config, i18n.language],
  );
  const country = regions.countries.find((c) => c.code === countryCode) ?? null;
  const currency = country?.currency ?? 'USD';

  // Seed the wizard once catalogue + session are available: hydrate region, name
  // and domain from the saved `customization_data` (Deploy parity).
  const initedRef = useRef(false);
  useEffect(() => {
    if (initedRef.current || regions.countries.length === 0) return;
    if (!session.loaded && !(session.error && !session.loading)) return;
    initedRef.current = true;

    const cd = session.data.customization_data;
    // Operating region for the deploy, in precedence order: the LIVE region carried over
    // from the configurator (saved on the session), else the marketing-site hand-off /
    // UI-language cue when it is live, else the first live market. A region is "live" by its
    // own `is_active`; a not-yet-live region (e.g. International here) is never auto-selected,
    // so onboarding always lands on a real, selectable market instead of a greyed default.
    const savedCountry = cd?.country ?? session.data.configuration_data?.countryCode ?? null;
    const isLive = (code: string | null | undefined): boolean =>
      !!code && regions.countries.some((c) => c.code === code && c.isActive);
    if (savedCountry && isLive(savedCountry)) {
      setCountryCode(savedCountry);
    } else {
      const cued = handoffRegion() ?? regionForLocale(i18n.language);
      if (cued && isLive(cued)) {
        setCountryCode(cued);
      } else if (!isLive(DEFAULT_COUNTRY)) {
        const firstLive = regions.countries.find((c) => c.isActive)?.code;
        if (firstLive) setCountryCode(firstLive);
      }
    }
    if (cd?.brandName) setSelectedName(cd.brandName);
    if (cd?.own_name !== undefined) setOwnName(cd.own_name);
    if (cd?.domain) setSelectedDomain(cd.domain);
    if (cd?.own_domain !== undefined) setOwnDomain(cd.own_domain);
    if (cd?.logo) setSelectedLogo(cd.logo);
    if (cd?.own_logo !== undefined) setOwnLogo(cd.own_logo);
    // Restore the editable INPUT fields too, not just the resolved selections, so a
    // saved manual name / connected domain reloads into its field instead of blank.
    if (cd?.brandName && cd?.own_name) setTypedNameState(cd.brandName);
    if (cd?.domain && cd?.own_domain) setConnectDomainState(cd.domain);
    setStatus('ready');

    // Fresh / partial session: write the own_* defaults (false) to the API right
    // away, so the flags exist before any user interaction. Preserves other fields.
    // Gate on `session.loaded` (not just `sessionId`, which survives from
    // localStorage across loads): the backend wholesale-REPLACES `data` on every
    // save, so firing this with an unloaded/empty `session.data` would POST a
    // payload with no `configuration_data` and permanently wipe the design keys
    // (siteSeedId/siteSkinId/artStyleId) the deploy needs. On a failed load we
    // degrade to catalog defaults WITHOUT persisting (no save) instead.
    const flagsMissing = cd?.own_name === undefined || cd?.own_domain === undefined || cd?.own_logo === undefined;
    if (flagsMissing && session.sessionId && session.loaded) {
      dispatch(
        sessionSave({
          id: session.sessionId,
          data: {
            ...session.data,
            customization_data: {
              ...cd,
              own_name: cd?.own_name ?? false,
              own_domain: cd?.own_domain ?? false,
              own_logo: cd?.own_logo ?? false,
            },
          },
        }),
      );
    }
  }, [regions.countries, session.loaded, session.error, session.loading, session.data, session.sessionId, i18n.language, dispatch]);

  // The intro is mobile-only — keep it in sync with the viewport.
  useEffect(() => {
    setDisplayIntro((prev) => (prev === isMobile ? prev : isMobile));
  }, [isMobile]);

  // Active wizard step is derived from the route.
  const stepKey = (location.pathname.split('/').filter(Boolean).pop() ?? 'country') as StepKey;
  const stepIndex = Math.max(0, WIZARD_STEPS.findIndex((s) => s.key === stepKey));
  const step = stepIndex + 1;

  // Canonical main-step CTA copy (desktop footer + mobile chrome share this).
  // Last step → go-live; step 3 → review; otherwise "Continue to <next step>".
  // The wizard step names (`Operating Area` / `Website Name` / `Logo` / `Go live`)
  // are a fixed UI vocabulary kept as stable English ids on WIZARD_STEPS; this
  // render-scoped map carries the translatable literals so they reach the catalog.
  const stepNameLabel = useCallback(
    (key: StepKey): string => {
      switch (key) {
        case 'country':
          return t('Operating Area');
        case 'brand':
          return t('Website Name');
        case 'logo':
          return t('Logo');
        case 'deploy':
          return t('Go live');
        default:
          return '';
      }
    },
    [t],
  );

  const { mainNextLabel, mainNextPlaceholders } = useMemo<{
    mainNextLabel: string;
    mainNextPlaceholders?: Record<string, string>;
  }>(() => {
    if (step >= WIZARD_STEPS.length) return { mainNextLabel: t('Take your website live') };
    if (stepKey === 'logo') return { mainNextLabel: t('Continue to Review') };
    // The next step's chrome name is translated before folding it into the
    // finished CTA copy, so `mainNextLabel` is a complete localized string.
    const nextKey = WIZARD_STEPS[step]?.key;
    const nextName = nextKey ? stepNameLabel(nextKey) : '';
    return { mainNextLabel: t('Continue to {{name}}', { name: nextName }) };
  }, [step, stepKey, t, stepNameLabel]);

  // Step-2 sub-step lives in the URL hash (#name/#domain AI, #own-name/#own-domain
  // manual) so each is a real browser-history entry: the device/browser Back
  // traverses them, not just the in-app arrow. Manual = whichever family the hash names.
  const hashSub = location.hash.replace(/^#/, '');
  // The hash wins during active navigation; with no step-2 hash (fresh entry /
  // direct nav / reload of a bare path) the mode falls back to the seeded
  // `ownName` flag, so a restored session shows the saved AI/manual screen.
  const step2Sub: Step2Sub =
    stepKey === 'brand' && (['name', 'domain', 'own-name', 'own-domain'] as string[]).includes(hashSub)
      ? (hashSub as Step2Sub)
      : ownName
        ? 'own-name'
        : 'name';
  const manualMode = step2Sub === 'own-name' || step2Sub === 'own-domain';

  // Connect-domain sub-flow stage (also hash-driven, so device Back traverses it).
  const connectStage: 'input' | 'dns' | null =
    stepKey === 'brand' && hashSub === 'connect'
      ? 'input'
      : stepKey === 'brand' && hashSub === 'connect-dns'
        ? 'dns'
        : null;

  // Step-3 logo mode, like step 2: the hash wins (#own-logo = upload, #generated =
  // AI); with no logo hash it falls back to the seeded `ownLogo` flag so a restored
  // session shows the saved mode.
  const aiLogo = hashSub === 'own-logo' ? false : hashSub === 'generated' ? true : !ownLogo;

  // Normalised typed domain (drop scheme/path, lower-case) + a basic validity gate.
  const normalizedConnectDomain = normalizeDomain(connectDomain);
  // Best-effort policy on top: government / education / major-brand domains are refused.
  const connectDomainWellFormed = /^([a-z0-9-]+\.)+[a-z]{2,}$/.test(normalizedConnectDomain);
  const connectDomainBlocked = connectDomainWellFormed && getDomainBlockReason(normalizedConnectDomain) !== null;
  const connectDomainValid = connectDomainWellFormed && !connectDomainBlocked;

  const connectLoading = submitting || session.domainConnect.loading;
  const dnsRecords = useMemo(
    () => normalizeDnsRecords(session.domainConnect.config),
    [session.domainConnect.config],
  );
  // A targeted connect failure the input screen renders inline. Only ERR_1003
  // ("already used by another instance") is special-cased; every other error keeps
  // the existing fall-through to the manual-setup records screen. `'blocked'` is
  // the live front-end policy verdict on the typed value (see lib/domain-policy).
  const connectError: ConnectError = connectDomainBlocked
    ? 'blocked'
    : session.domainConnect.errorCode === DOMAIN_ALREADY_USED_CODE
      ? 'already-used'
      : null;

  // ── Selection setters (persist) ───────────────────────────────────────────
  const dirtyRef = useRef(false);
  // Logo-generation guards (reset when the logo step's inputs change, the mode is
  // toggled, or an earlier step cascades a reset).
  const requestedLogos = useRef(false);
  const themesSliceStart = useRef(0);
  // Themes already requested this session. First request of a theme uses the cache
  // (fast, no generation cost); any revisit — the deck wrapping around, or "creative"
  // reappearing in every batch — bypasses it so the user gets a genuinely new logo
  // instead of a cache-hit duplicate that the dedup would silently drop.
  const requestedThemes = useRef<Set<string>>(new Set());
  // How many "top-up" rounds we've run for the current batch — when the dedup drops
  // a collision, we request more themes to refill to `logosCount`; bounded so a
  // brand that genuinely can't yield enough distinct logos doesn't loop forever.
  const topUpRoundsRef = useRef(0);
  // Manual-upload concurrency guards: a generation counter so a superseding action
  // (a fresh pick, a mode toggle, a cascade reset) invalidates an in-flight
  // upload's late state writes, plus a mounted flag so nothing writes after unmount.
  const logoOpRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Cascade resets (downstream-only): changing step-1 data resets step 2 + step 3;
  // changing step-2 data resets step 3; changing step-3 data resets nothing.
  const resetLogo = useCallback((): void => {
    logoOpRef.current += 1; // supersede any in-flight upload so its late writes no-op
    setLogoProcessing(false); // and drop the overlay (this cascade can fire mid-upload)
    setOwnLogo(false);
    setSelectedLogo(null);
    setUploadedFile(null);
    setLogoError(null);
    requestedLogos.current = false;
    themesSliceStart.current = 0;
    requestedThemes.current.clear();
    dispatch(clearLogosSuggestions());
  }, [dispatch]);
  const resetBrand = useCallback((): void => {
    setSelectedName(null);
    setTypedNameState('');
    setOwnName(false);
    setNameStart(0);
    setSelectedDomain(null);
    setConnectDomainState('');
    setOwnDomain(false);
    setEntriSuccess(false);
  }, []);

  const setCountry = useCallback(
    (code: string): void => {
      if (code === countryCode) return; // no change → no cascade
      dirtyRef.current = true;
      setCountryCode(code);
      resetBrand(); // step-1 change resets step 2…
      resetLogo(); // …and step 3
    },
    [countryCode, resetBrand, resetLogo],
  );
  const setName = useCallback(
    (name: string): void => {
      if (name === selectedName) return;
      dirtyRef.current = true;
      setSelectedName(name);
      setOwnName(false); // a picked AI/generated name is NOT the user's own name
      setSelectedDomain(null); // a new name invalidates the chosen domain
      setOwnDomain(false); // …and clears the "own domain" provenance
      resetLogo(); // step-2 change resets step 3
    },
    [selectedName, resetLogo],
  );
  const setDomain = useCallback(
    (domain: string): void => {
      if (domain === selectedDomain) return;
      dirtyRef.current = true;
      setSelectedDomain(domain);
      setOwnDomain(false); // a picked AI/generated suggestion is NOT the user's own domain
      resetLogo(); // step-2 change resets step 3
    },
    [selectedDomain, resetLogo],
  );
  const setManualMode = useCallback(
    (on: boolean): void => {
      dirtyRef.current = true;
      // Switching the step-2 mode resets ALL of step 2's data AND cascades to step 3.
      resetBrand();
      resetLogo();
      // Replace (not push) so toggling the subflow doesn't stack history — Back from
      // the first sub-step then goes straight to step 1.
      navigate(`${WIZARD_STEPS[1].path}#${on ? 'own-name' : 'name'}`, { replace: true });
    },
    [resetBrand, resetLogo, navigate],
  );
  const setTypedName = useCallback(
    (name: string): void => {
      if (name === typedName) return;
      dirtyRef.current = true;
      setTypedNameState(name);
      setOwnName(true); // the user typed their own brand name → own_name = true
      setSelectedDomain(null); // a new typed name invalidates the chosen domain
      setOwnDomain(false);
      resetLogo(); // step-2 change resets step 3
    },
    [typedName, resetLogo],
  );

  // ── Connect-domain sub-flow ───────────────────────────────────────────────
  const openConnectDomain = useCallback(() => {
    navigate(`${WIZARD_STEPS[1].path}#connect`);
  }, [navigate]);

  const setConnectDomain = useCallback(
    (value: string): void => {
      setConnectDomainState(value); // the field always reflects what's typed
      // Editing the domain clears a prior "can't connect" error so the inline note
      // doesn't linger over a domain the user is already changing.
      if (session.domainConnect.error || session.domainConnect.errorCode) dispatch(clearDomainConnect());
      // Persist the typed domain live (debounced by the save effect), like the
      // manual brand name: it lands in `customization_data.domain` as the user types.
      const normalized = normalizeDomain(value);
      if (normalized === selectedDomain) return; // only cascade when the domain actually changes
      setSelectedDomain(normalized || null);
      setOwnDomain(Boolean(normalized)); // it's the user's own domain (cleared when empty)
      dirtyRef.current = true;
      resetLogo(); // step-2 change resets step 3
    },
    [selectedDomain, resetLogo, session.domainConnect.error, session.domainConnect.errorCode, dispatch],
  );

  const editConnectDomain = useCallback(() => {
    setEntriSuccess(false); // re-editing the domain invalidates a prior connection
    navigate(`${WIZARD_STEPS[1].path}#connect`, { replace: true });
  }, [navigate]);

  // Submit the typed domain: persist it as the chosen domain, then fetch the
  // domain-connect config (its `dnsRecords` are what the records screen shows).
  // Navigation to the records screen happens once the fetch settles (effect below).
  const submitConnectDomain = useCallback(() => {
    if (!connectDomainValid || !session.sessionId) return;
    dirtyRef.current = true;
    setEntriSuccess(false); // a freshly submitted domain isn't connected yet
    setSelectedDomain(normalizedConnectDomain);
    setOwnDomain(true); // the user submitted THEIR OWN domain → own_domain = true
    setCheck(null);
    setSubmitting(true);
    dispatch(clearDomainConnect());
    dispatch(sessionDomainConnectConfig({ event_type: 'config', event_data: '' }));
  }, [connectDomainValid, normalizedConnectDomain, session.sessionId, dispatch]);

  // Once the in-flight config fetch settles, reveal the records screen (a new hash
  // history entry, so Back returns to the input). On error we still advance — the
  // screen falls back to the manual-setup message with whatever records resolved.
  useEffect(() => {
    if (!submitting || session.domainConnect.loading) return;
    // "Domain already used" (ERR_1003) is a dead end: there are no DNS records to
    // set up, so DON'T advance to the records screen (whose "manual setup" copy
    // would be false + misleading). Settle on the input screen — `connectError`
    // drives the inline "used by another instance" message.
    if (session.domainConnect.errorCode === DOMAIN_ALREADY_USED_CODE) {
      setSubmitting(false);
      return;
    }
    if (session.domainConnect.loaded || session.domainConnect.error) {
      setSubmitting(false);
      navigate(`${WIZARD_STEPS[1].path}#connect-dns`);
    }
  }, [
    submitting,
    session.domainConnect.loading,
    session.domainConnect.loaded,
    session.domainConnect.error,
    session.domainConnect.errorCode,
    navigate,
  ]);

  // On the records screen, detect the registrar (entrijs) to choose the automatic
  // vs manual-setup copy. Any failure falls back to manual (records always show).
  useEffect(() => {
    if (connectStage !== 'dns' || check) return undefined;
    const config = session.domainConnect.config;
    if (!config || !selectedDomain) return undefined;
    let cancelled = false;
    Promise.resolve(checkDomain(selectedDomain, config))
      .then((res) => {
        if (cancelled) return;
        const provider = res?.provider ?? null;
        const setupType = (res?.setupType ?? '').toLowerCase();
        const auto = Boolean(provider) && setupType !== 'manual' && setupType !== 'nosetup';
        setCheck({ provider, auto });
      })
      .catch(() => {
        if (!cancelled) setCheck({ provider: null, auto: false });
      });
    return () => {
      cancelled = true;
    };
  }, [connectStage, session.domainConnect.config, selectedDomain, check]);

  const connectProvider = check?.provider ?? null;
  const connectAutoSupported = check?.auto ?? false;

  // ── entrijs Domain Connect (automatic setup) ──────────────────────────────
  // Duplicated 1:1 from src/components/modules/deploy/hooks/useDomainConnect.ts
  // (the Deploy module's hook) — same logic, copied here rather than imported.
  const handleSuccess = useCallback(
    (event: Event) => {
      const { detail } = event as CustomEvent<EntriSuccessEventDetail>;

      const eventData = JSON.stringify(detail);

      // The user linked their own domain: own_domain stays true and the connection
      // succeeded, so unlock "Continue to Logo".
      setOwnDomain(true);
      setEntriSuccess(true);
      dirtyRef.current = true;

      dispatch(
        sessionDomainConnectSetupSuccess({
          event_type: 'success',
          event_data: eventData,
        }),
      );
    },
    [dispatch],
  );

  const handleClose = useCallback(
    (event: Event) => {
      const { detail } = event as CustomEvent<EntriCloseEventDetail>;

      const eventData = JSON.stringify(detail);

      if (detail.success) {
        setOwnDomain(true);
        setEntriSuccess(true);
        dirtyRef.current = true;
        dispatch(
          sessionDomainConnectSetupSuccess({
            event_type: 'success-close-modal',
            event_data: eventData,
          }),
        );
      } else if (detail.error) {
        dispatch(
          sessionDomainConnectSetupFailure({
            event_type: 'failure-close-modal',
            event_data: eventData,
          }),
        );
      } else {
        dispatch(
          sessionDomainConnectSetupFailure({
            event_type: 'cancel-close-modal',
            event_data: eventData,
          }),
        );
      }

      setEntriOpen(false);
    },
    [dispatch],
  );

  useEffect(() => {
    // Triggered when the user successfully completes domain setup.
    window.addEventListener('onSuccess', handleSuccess);
    // Triggered when the modal is closed, whether successful or not.
    window.addEventListener('onEntriClose', handleClose);

    return () => {
      window.removeEventListener('onSuccess', handleSuccess);
      window.removeEventListener('onEntriClose', handleClose);
    };
  }, [handleClose, handleSuccess]);

  // 1:1 with the hook's handleOpenConnect (exposed here as `openEntri`).
  const openEntri = useCallback(() => {
    const config = session.domainConnect.config;
    const domain = session.data.customization_data?.domain;
    if (session.domainConnect.loaded && !isNil(config) && !isNil(domain)) {
      // Debugging only, for now
      void checkDomain(domain, config).then((response) => {
        setDomainCheck(response);
      });

      // Adjust config from back-end with some front-end settings
      const fullEntriConfig: EntriConfig = {
        ...config,
        prefilledDomain: domain,
        locale: i18n.language,
      };

      // Open Entri modal
      setEntriOpen(true);

      showEntri(fullEntriConfig)
        .then(() => {
          dispatch(
            sessionDomainConnectSetupBegin({
              event_type: 'initialize',
              event_data: JSON.stringify(fullEntriConfig),
            }),
          );
        })
        // Quick fix: if Entri/the API fails, re-enable the button instead of
        // leaving it stuck on "Connecting …".
        .catch(() => {
          setEntriOpen(false);
        });
    }
  }, [
    dispatch,
    i18n.language,
    session.data.customization_data?.domain,
    session.domainConnect.config,
    session.domainConnect.loaded,
  ]);

  // The resolved brand name. Keyed on the `ownName` provenance flag (NOT the
  // hash-derived `manualMode`, which drops to false inside the connect sub-flow):
  // own name → the live typed value, falling back to the seeded `selectedName`
  // (typedName is empty right after a reload); otherwise the AI-picked name.
  const effectiveName = ownName ? typedName.trim() || selectedName : selectedName;

  // ── Step 3 — logo generation + upload ─────────────────────────────────────
  // Cap at `logosCount` (the batch size): never render more than a batch's worth,
  // even if extra results linger (e.g. a superseded batch's late websocket logos).
  const logos = useMemo<LogoSuggestion[]>(
    () =>
      logosRaw
        .map((l) => ({ cdnUrl: l.logo?.cdn_url ?? '', description: l.logo?.description ?? '' }))
        .filter((l) => l.cdnUrl)
        .slice(0, logosCount),
    [logosRaw, logosCount],
  );

  // `logosSuggestions` is GLOBAL redux state that survives navigation, so a prior
  // run of the flow (configurator → onboarding) leaves its generated logos behind.
  // Re-entering with the same brand would pile the new batch on top of the old
  // cached ones. Clear once per mount — a fresh flow entry starts from an empty
  // grid; in-session step navigation keeps this provider mounted, so a generated
  // batch still persists while the user moves between steps.
  useEffect(() => {
    dispatch(clearLogosSuggestions());
  }, [dispatch]);

  // Fetch the theme list once a session exists (logos are generated from themes).
  // This used to be a one-shot latch: if that first request failed or was slow,
  // `logoThemes` stayed empty forever, so `fetchLogoSuggestions` silently no-oped
  // (no "creative" theme) — the grid stayed blank AND "Refresh" did nothing (it
  // calls the same fn), with no network request and no error. Now it bounded-
  // retries, and "Refresh" can force a fresh attempt via `ensureThemes`.
  //
  // The list is ranked FOR this site, so the fetch waits until the site is known:
  // `status === 'ready'` means the session DATA is loaded and the region resolved.
  // `sessionId` alone is not enough — it survives in storage from the last visit, so it
  // is set before the design keys are, and a fetch at that point sends an empty context.
  // A changed context (the owner picks another region in step 1) re-ranks the list.
  const themesAttempts = useRef(0);
  const themesContextKey = useRef<string | null>(null);
  const ensureThemes = useCallback((): void => {
    if (status !== 'ready' || !session.sessionId || !configState.config || loadingThemes) return;
    const contextKey = JSON.stringify(siteContext);
    if (logoThemes.length > 0 && themesContextKey.current === contextKey) return;
    if (themesContextKey.current !== contextKey) themesAttempts.current = 0;
    if (themesAttempts.current >= MAX_THEME_FETCH_ATTEMPTS) return;
    themesAttempts.current += 1;
    themesContextKey.current = contextKey;
    themesSliceStart.current = 0; // new order → the window restarts at the best fit
    dispatch(fetchThemes(siteContext));
  }, [status, session.sessionId, configState.config, loadingThemes, logoThemes.length, siteContext, dispatch]);

  useEffect(() => {
    ensureThemes();
  }, [ensureThemes]);

  // Resolve the generator seed (colours + brand) and dispatch one `fetchLogo` per
  // theme. Each POST returns immediately; the images arrive over the websocket into
  // `mlServices.logosSuggestions` (deduped by cdn_url). Returns false when a
  // prerequisite (brand) isn't ready yet. Shared by the initial batch + the top-up.
  const dispatchLogosForThemes = useCallback(
    (themes: string[]): boolean => {
      // Seed colours come from the configurator theme; some sessions reach the
      // customizer without it (no `configuration_data.tokens`), so fall back to a
      // default palette — the generate API rejects empty/missing colours (422).
      const tokenValues = getValuesFromTokens(session.data.configuration_data?.tokens) ?? CUST_LOGO_SEED_FALLBACK;
      // Fall back to the saved session brand name: on a reload `selectedName` (and
      // so `effectiveName`) can lag the persisted `customization_data.brandName`.
      const brand = effectiveName ?? session.data.customization_data?.brandName ?? '';
      if (!brand || themes.length === 0) return false;
      themes.forEach((theme) => {
        dispatch(
          fetchLogo({
            brandName: brand,
            primaryColor: tokenValues.primaryColor,
            secondaryColor: tokenValues.secondaryColor,
            background: tokenValues.background,
            theme,
            siteContext,
            bypassCache: requestedThemes.current.has(theme),
            // Server-side removal: the websocket delivers the transparent cutout
            // directly (cached end-to-end), so no per-logo /remove-background call.
            removeBackground: true,
          }),
        );
        requestedThemes.current.add(theme);
      });
      return true;
    },
    [
      dispatch,
      session.data.configuration_data?.tokens,
      session.data.customization_data?.brandName,
      effectiveName,
      siteContext,
    ],
  );

  // Request a batch of logos: (count − 1) themes in a wrapping window + the one
  // "creative" theme. Ported from the Deploy module.
  const fetchLogoSuggestions = useCallback(() => {
    const creativeTheme = findExtraElement(logoThemes, logoOptions);
    if (!creativeTheme) return;
    const { nextSlice, nextStartIndex } = getCircularSlice(logoThemes, logosCount - 1, themesSliceStart.current);
    if (!dispatchLogosForThemes([...nextSlice, creativeTheme])) return;

    requestedLogos.current = true;
    topUpRoundsRef.current = 0; // fresh batch → reset the top-up budget
    themesSliceStart.current = nextStartIndex;
  }, [logoThemes, logoOptions, logosCount, dispatchLogosForThemes]);

  // Top-up: request `count` more themes (no "creative" — it's already in the batch)
  // from where the slice left off, to refill after the dedup dropped collisions.
  const fetchMoreLogos = useCallback(
    (count: number) => {
      if (count <= 0 || logoThemes.length === 0) return;
      const { nextSlice, nextStartIndex } = getCircularSlice(logoThemes, count, themesSliceStart.current);
      if (!dispatchLogosForThemes(nextSlice)) return;
      themesSliceStart.current = nextStartIndex;
    },
    [logoThemes, dispatchLogosForThemes],
  );

  // On entering step 3 in AI mode, request the first batch (once themes exist).
  useEffect(() => {
    if (step === 3 && aiLogo && !requestedLogos.current && logoThemes.length > 0) {
      fetchLogoSuggestions();
    }
  }, [step, aiLogo, logoThemes.length, fetchLogoSuggestions]);

  // Refill the grid to `logosCount` once a batch/round settles short — the dedup
  // drops logos that collided on cdn_url (e.g. two themes that cache-hit the same
  // asset), leaving fewer than a full grid. Pull the NEXT themes (cache-hits reuse
  // already-generated logos; only genuinely new themes generate). Bounded so a
  // brand that can't yield `logosCount` distinct logos settles for fewer instead
  // of looping. Only runs after an initial batch (`requestedLogos`).
  //
  // Gate on the loading true→false TRANSITION, not just on "not loading": when the
  // entry effect dispatches the initial batch, this effect runs in the SAME commit
  // with the stale pre-dispatch render values (`loadingLogos` false, empty grid) —
  // while `requestedLogos.current`, a ref, is already true — so a plain not-loading
  // guard saw a 4-logo "gap" and fired a full duplicate batch on step entry.
  const logosWereLoading = useRef(false);
  useEffect(() => {
    const settled = logosWereLoading.current && !loadingLogos;
    logosWereLoading.current = loadingLogos;
    if (!settled || step !== 3 || !aiLogo || !requestedLogos.current) return;
    const gap = logosCount - logos.length;
    if (gap <= 0) {
      topUpRoundsRef.current = 0; // full grid → reset budget for the next batch
      return;
    }
    if (topUpRoundsRef.current >= MAX_LOGO_TOPUP_ROUNDS) return; // can't fill — accept fewer
    topUpRoundsRef.current += 1;
    fetchMoreLogos(gap);
  }, [step, aiLogo, loadingLogos, logos.length, logosCount, fetchMoreLogos]);

  // Changing the brand name / region invalidates the generated logos → re-request.
  const logoSeedRef = useRef<{ name: string | null; country: string } | null>(null);
  useEffect(() => {
    if (status !== 'ready') return;
    const prev = logoSeedRef.current;
    logoSeedRef.current = { name: effectiveName, country: countryCode };
    if (!prev) return;
    if (prev.name !== effectiveName || prev.country !== countryCode) {
      requestedLogos.current = false;
      themesSliceStart.current = 0;
      requestedThemes.current.clear();
      if (aiLogo) setSelectedLogo(null);
      dispatch(clearLogosSuggestions());
    }
  }, [status, effectiveName, countryCode, aiLogo, dispatch]);

  const setAiLogo = useCallback(
    (on: boolean): void => {
      dirtyRef.current = true;
      logoOpRef.current += 1; // supersede an in-flight upload + clear its overlay
      setLogoProcessing(false);
      requestedLogos.current = false;
      themesSliceStart.current = 0;
      requestedThemes.current.clear();
      setOwnLogo(!on); // the flag tracks the mode (persisted as own_logo)
      setSelectedLogo(null); // switching mode resets this step's selection
      setUploadedFile(null);
      setLogoError(null);
      dispatch(clearLogosSuggestions());
      // The mode lives in the hash (replace, so the toggle doesn't stack history).
      navigate(`${WIZARD_STEPS[2].path}#${on ? 'generated' : 'own-logo'}`, { replace: true });
    },
    [dispatch, navigate],
  );

  const selectLogo = useCallback(
    (cdnUrl: string): void => {
      dirtyRef.current = true;
      setOwnLogo(false); // a generated logo is not the user's own upload
      setUploadedFile(null);
      setSelectedLogo(cdnUrl);
      // Engagement ping (fire-and-forget): the generation API ranks themes by
      // which logos owners actually pick. Uploads don't ping — no theme to credit.
      dispatch(reportLogoSelected({ cdnUrl }));
    },
    [dispatch],
  );

  const refreshLogos = useCallback(() => {
    dispatch(clearLogosSuggestions());
    // If themes never loaded (the usual cause of an empty grid + a "dead" refresh),
    // retry them — the entry effect requests a batch the moment they arrive. Reset
    // the attempt budget so the user-initiated retry isn't blocked by earlier fails.
    if (logoThemes.length === 0) {
      themesAttempts.current = 0;
      requestedLogos.current = false;
      ensureThemes();
      return;
    }
    fetchLogoSuggestions();
  }, [dispatch, logoThemes.length, ensureThemes, fetchLogoSuggestions]);

  // Upload the picked file to the session art store, then strip its background;
  // the processed CDN URL becomes the selected logo. The whole-screen overlay
  // (logoProcessing) covers the wizard for the duration of the synchronous job.
  const onSelectLogoFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const files = event?.target?.files;
      if (!files || files.length === 0) return;
      // Clear the picker after each attempt so re-selecting the SAME file fires a
      // fresh change (the browser suppresses it when the value is unchanged).
      const inputEl = event.target;
      const clearPicker = (): void => {
        if (inputEl) inputEl.value = '';
      };
      // Any new pick supersedes a prior in-flight upload (its late writes no-op).
      const op = (logoOpRef.current += 1);
      const alive = (): boolean => op === logoOpRef.current && mountedRef.current;

      // First file within the size + type limits; otherwise surface a clear error.
      const file = Array.from(files).find(
        (f) => f.size < MAX_LOGO_SIZE && ALLOWED_LOGO_TYPES.some((type) => f.type.includes(type)),
      );
      if (!file) {
        setLogoProcessing(false); // never strand the overlay (e.g. rapid re-pick)
        setLogoError('unsupported');
        clearPicker();
        return;
      }
      if (!session.sessionId) {
        clearPicker();
        return;
      }

      setLogoError(null);
      setLogoProcessing(true);
      try {
        const brand = effectiveName ?? session.data.customization_data?.brandName ?? '';
        const uploaded = await dispatch(uploadLogo({ file, brandName: brand })).unwrap();
        if (!alive()) return; // a reset / mode-toggle / fresh pick superseded us
        const uploadedUrl = uploaded.data.image?.cdn_url;
        if (!uploadedUrl) throw new Error('upload returned no url');

        const processed = await dispatch(
          removeLogoBackground({ imageUrl: uploadedUrl, brandName: brand, size: 'preview' }),
        ).unwrap();
        if (!alive()) return;
        // Fall back to the uploaded URL if background removal yields nothing usable.
        const finalUrl = processed.data.image?.cdn_url || uploadedUrl;

        dirtyRef.current = true;
        setOwnLogo(true); // an uploaded file is the user's own logo
        setSelectedLogo(finalUrl);
        setUploadedFile(file);
      } catch {
        if (alive()) setLogoError('failed');
      } finally {
        if (alive()) setLogoProcessing(false); // a superseding action already cleared it
        clearPicker();
      }
    },
    [dispatch, effectiveName, session.sessionId, session.data.customization_data?.brandName],
  );

  const resetUploadedLogo = useCallback(() => {
    dirtyRef.current = true;
    setSelectedLogo(null);
    setUploadedFile(null);
    setLogoError(null);
  }, []);

  // ── Step 4 — deploy / "Go live" ───────────────────────────────────────────
  // Only ever frame / open an http(s) preview. The backend-set `previewUrl` is otherwise
  // fed verbatim to an <iframe src> and window.open; a `javascript:`/`data:` value would
  // run script in the studio origin. Reject any non-http(s) scheme to '' here.
  const previewUrl = safeHttpUrl(session.data.configuration_data?.previewUrl);
  const previewTokens = session.data.configuration_data?.tokens;
  // Canonical brand name for the summary: the live effective name, else the saved
  // session value (covers the manual subflow where it lives in `typedName`).
  const brandName = effectiveName ?? session.data.customization_data?.brandName ?? null;
  const deploy = session.deploy;
  const deployPhase: 'review' | 'deploying' | 'done' =
    deploy.loaded || session.status === SessionStatus.done
      ? 'done'
      : deploy.loading || session.status === SessionStatus.deployment_in_progress
        ? 'deploying'
        : 'review';

  const startDeploy = useCallback(async () => {
    if (!session.sessionId || !session.loaded) return;

    // Self-heal the design keys before deploying. The deploy endpoint requires
    // configuration_data.{siteSeedId,siteSkinId,artStyleId} (chosen in the
    // pre-payment configurator), but a historical race could drop them — and
    // because the backend wholesale-REPLACES `data` on every save, once dropped
    // they stay dropped, so deploy fails with MISSING_SESSION_DATA_KEYS. If they
    // are missing, re-derive them from the saved colour `tokens` (matching the
    // catalog, mirroring the configurator's own restore) — or the catalog default
    // when no tokens survive — and persist them BEFORE deploying. The save must
    // land server-side first (wholesale replace), so we await it.
    const cd = session.data.configuration_data;
    const designMissing = cd?.siteSeedId == null || cd?.siteSkinId == null || cd?.artStyleId == null;
    if (designMissing && configState.config) {
      try {
        const catalog = transformCatalog(configState.config as unknown as Raw, currency, i18n.language);
        const savedTokens = cd?.tokens;
        const theme =
          (savedTokens && catalog.themes.find((th) => th.colors.some((c) => isEqual(c.tokens, savedTokens)))) ||
          catalog.themes[0] ||
          null;
        const color =
          (savedTokens && theme?.colors.find((c) => isEqual(c.tokens, savedTokens))) || theme?.colors[0] || null;
        const style = theme && color ? availableStyles(catalog, theme, color)[0] ?? null : null;
        if (theme && color && style) {
          await dispatch(
            sessionSave({
              id: session.sessionId,
              data: {
                ...session.data,
                configuration_data: {
                  ...cd,
                  siteSeedId: theme.seedId,
                  siteSkinId: Number(color.id),
                  artStyleId: Number(style.id),
                  tokens: color.tokens,
                },
              },
            }),
          ).unwrap();
        }
      } catch {
        // Best-effort: if the repair save fails, fall through and let deploy run —
        // it surfaces the same MISSING_SESSION_DATA_KEYS error, no worse than before.
      }
    }

    // Remember this deploy session so the brand switcher adopts the brand it
    // creates the moment it appears in the list (Deploy-module parity).
    writePendingBrandSessionId(session.sessionId);
    dispatch(sessionDeploy({ id: session.sessionId }));
  }, [session.sessionId, session.loaded, session.data, configState.config, currency, i18n.language, dispatch]);

  // Persist region / name / domain to the session's `customization_data`.
  // Free-text fields (brand name / domain) DEBOUNCE the save by 500ms so we don't
  // POST on every keystroke; selections (country / suggestion picks / logo / the
  // own_* flags) save immediately. Mirrors the Deploy module's save effect.
  useEffect(() => {
    if (!dirtyRef.current || status !== 'ready') return undefined;
    const sessionId = session.sessionId;
    if (!sessionId || !session.loaded) return undefined;
    const cd = session.data.customization_data;

    const next = {
      country: countryCode,
      brandName: effectiveName ?? undefined,
      domain: selectedDomain ?? undefined,
      logo: selectedLogo ?? undefined,
      own_name: ownName,
      own_domain: ownDomain,
      own_logo: ownLogo,
    };

    const same =
      cd?.country === next.country &&
      (cd?.brandName ?? undefined) === next.brandName &&
      (cd?.domain ?? undefined) === next.domain &&
      (cd?.logo ?? undefined) === next.logo &&
      (cd?.own_name ?? false) === next.own_name &&
      (cd?.own_domain ?? false) === next.own_domain &&
      (cd?.own_logo ?? false) === next.own_logo;
    if (same) return undefined;

    const isNameChanged = (cd?.brandName ?? undefined) !== next.brandName;
    const isDomainChanged = (cd?.domain ?? undefined) !== next.domain;
    const needsDebounce = isNameChanged || isDomainChanged;

    const saveData = (): void => {
      dirtyRef.current = false;
      dispatch(
        sessionSave({
          id: sessionId,
          data: {
            ...session.data,
            customization_data: { ...cd, ...next },
          },
        }),
      );
    };

    if (needsDebounce) {
      const id = window.setTimeout(saveData, 500);
      return () => window.clearTimeout(id);
    }

    saveData();
    return undefined;
  }, [
    countryCode,
    effectiveName,
    selectedDomain,
    selectedLogo,
    ownName,
    ownDomain,
    ownLogo,
    status,
    session.sessionId,
    session.loaded,
    session.data,
    dispatch,
  ]);

  // ── AI suggestions: fetch on entering step 2 (per region) ─────────────────
  const requestedNamesCountry = useRef<string | null>(null);
  useEffect(() => {
    if (step !== 2 || !countryCode || !session.sessionId) return;
    if (requestedNamesCountry.current === countryCode) return;
    requestedNamesCountry.current = countryCode;
    dispatch(fetchDomainsSuggestionsByCountry({ country: countryCode }));
  }, [step, countryCode, session.sessionId, dispatch]);

  // Client-side window: 3 AI NAMES at a time, wrapping around the array (the
  // "Refresh suggestions" button on the names section pages through them locally).
  const names = useMemo(() => circularSlice(suggestions, PAGE_SIZE, nameStart), [suggestions, nameStart]);
  // Both subflows source domains from the same godaddy `suggest` batch
  // (`manualDomains`, fetched at DOMAIN_FETCH_LIMIT per query) — one live
  // availability path, replacing the AI endpoint's pre-baked, potentially stale
  // `domains[]` bundle. We window 3 at a time; "Refresh suggestions" pages through
  // the batch locally so the user sees fresh variety without re-hitting the API.
  const domains = useMemo(() => circularSlice(manualDomains, PAGE_SIZE, domainStart), [manualDomains, domainStart]);

  // Manual subflow: debounce the typed name, then fetch domain suggestions for it
  // from godaddy `suggest` (the change "event" fires once the user stops typing →
  // skeleton → domains).
  useEffect(() => {
    if (!manualMode || step !== 2) return undefined;
    const q = typedName.trim();
    if (!q || !countryCode || !session.sessionId) return undefined;
    const id = window.setTimeout(() => {
      resetDomainPager(); // fresh batch → back to the first page (clears nav history)
      dispatch(clearDomainsSuggestionsByDomain());
      dispatch(fetchDomainsSuggestionsByDomain({ query: q, limit: DOMAIN_FETCH_LIMIT, country: countryCode }));
    }, 500);
    return () => window.clearTimeout(id);
  }, [manualMode, step, typedName, countryCode, session.sessionId, dispatch, resetDomainPager]);

  // AI subflow: when a NAME is picked, fetch its domains from the SAME godaddy
  // `suggest` endpoint the manual subflow uses (one live availability check per
  // pick) — so both subflows share one domain-suggestion path. No debounce: a
  // name-pick is a discrete action, not per-keystroke typing. Also fires on reload,
  // when `selectedName` is seeded from the saved session, refreshing its domains.
  useEffect(() => {
    if (manualMode || step !== 2) return;
    const q = (selectedName ?? '').trim();
    if (!q || !countryCode || !session.sessionId) return;
    resetDomainPager(); // fresh batch → back to the first page (clears nav history)
    dispatch(clearDomainsSuggestionsByDomain());
    dispatch(fetchDomainsSuggestionsByDomain({ query: q, limit: DOMAIN_FETCH_LIMIT, country: countryCode }));
  }, [manualMode, step, selectedName, countryCode, session.sessionId, dispatch, resetDomainPager]);

  const refreshNames = useCallback(() => {
    setNameStart((s) => nextSliceStart(suggestions.length, PAGE_SIZE, s));
    setSelectedName(null);
    setOwnName(false);
    setSelectedDomain(null);
    setOwnDomain(false);
    dirtyRef.current = true;
    resetLogo(); // step-2 change resets step 3
  }, [suggestions.length, resetLogo]);

  // "Refresh suggestions" on the DOMAIN section pages LOCALLY through the already-
  // fetched batch (DOMAIN_FETCH_LIMIT domains, PAGE_SIZE at a time, wrapping) — no
  // re-fetch, so each press shows a fresh trio instead of re-hitting the API and
  // risking the same top results. The pager runs a 0.4s skeleton flash so the swap
  // is visible and records the page for the back/forward arrows. Also clears the
  // chosen domain. A fresh batch is fetched only when the query changes.
  const refreshDomains = useCallback(() => {
    setSelectedDomain(null);
    setOwnDomain(false);
    dirtyRef.current = true;
    resetLogo(); // step-2 change resets step 3
    nextDomainPage();
  }, [nextDomainPage, resetLogo]);

  // ── Navigation (ported from the Deploy wizard) ────────────────────────────
  const goNext = useCallback(() => {
    if (displayIntro && isMobile) {
      setDisplayIntro(false);
      return;
    }
    // Connect-domain sub-flow: input → fetch+reveal records; records → step 3 (logo).
    if (connectStage === 'input') {
      submitConnectDomain();
      return;
    }
    if (connectStage === 'dns') {
      navigate(`${WIZARD_STEPS[2].path}#${ownLogo ? 'own-logo' : 'generated'}`);
      return;
    }
    // Mobile step 2: advance the sub-step by PUSHING a hash history entry so Back
    // pops back to it. The domain sub-step is the connect screen when own_domain.
    if (step === 2 && isMobile) {
      if (step2Sub === 'name') {
        navigate(`${WIZARD_STEPS[1].path}#domain`);
        return;
      }
      if (step2Sub === 'own-name') {
        navigate(`${WIZARD_STEPS[1].path}#${ownDomain ? 'connect' : 'own-domain'}`);
        return;
      }
    }
    const next = WIZARD_STEPS[stepIndex + 1];
    if (!next) return;
    // Enter step 2 on the name & domain ROOT in the mode saved in the session
    // (flag-driven restore): the manual name & domain screen when own_name, else the
    // AI suggestions. Even when own_domain was saved, do NOT deep-link into the
    // "Link your own domain" subpage — it's reached FROM this screen ("I have my own
    // domain"), and jumping straight there skips the name & domain page.
    if (next.key === 'brand') {
      const target = ownName ? 'own-name' : 'name';
      navigate(`${next.path}#${target}`);
      return;
    }
    // Enter step 3 in the saved logo mode (upload when own_logo, else AI).
    if (next.key === 'logo') {
      navigate(`${next.path}#${ownLogo ? 'own-logo' : 'generated'}`);
      return;
    }
    navigate(next.path);
  }, [
    displayIntro,
    isMobile,
    step,
    step2Sub,
    stepIndex,
    navigate,
    connectStage,
    submitConnectDomain,
    ownName,
    ownDomain,
    ownLogo,
  ]);

  const goBack = useCallback(() => {
    // Once deployment has started there is no way back — it's a one-way door.
    if (step === 4 && deployPhase !== 'review') return;
    // Desktop connect-domain sub-flow: Back returns to the name & domain screen it
    // was launched from (the input → the manual name & domain screen; the records
    // screen → the input), navigating EXPLICITLY rather than popping history. A
    // restored own_domain session enters step 2 straight on #connect (goNext skips
    // #own-name), so a plain history pop would jump back to step 1 instead of the
    // name & domain screen. (Mobile always reaches connect via the own-name →
    // own-domain sub-steps, so its history pop is already correct.)
    if (!isMobile && connectStage) {
      navigate(`${WIZARD_STEPS[1].path}#${connectStage === 'dns' ? 'connect' : 'own-name'}`, { replace: true });
      return;
    }
    // Mobile step 1: Back re-opens the intro instead of leaving the wizard.
    if (step === 1 && isMobile && !displayIntro) {
      setDisplayIntro(true);
      return;
    }
    if (step === 1) return; // nothing before step 1 (desktop, or intro already shown)
    // Every forward move was a history push (step routes + step-2 sub-step hashes),
    // so a single Back pops to the exact previous position — same as the device Back.
    navigate(-1);
  }, [step, isMobile, displayIntro, navigate, deployPhase, connectStage]);

  const canAdvance = useMemo(() => {
    // Connect-domain sub-flow gates: input needs a valid domain and must not be
    // mid-fetch (so the mobile CTA disables while linking, like the desktop button);
    // the records screen ("Continue to Logo") unlocks only after entri succeeds.
    if (connectStage === 'input') return connectDomainValid && !connectLoading;
    if (connectStage === 'dns') return entriSuccess;
    if (step === 1) return Boolean(country);
    if (step === 2) {
      if (!isMobile) {
        return manualMode
          ? Boolean(effectiveName) && Boolean(selectedDomain)
          : Boolean(selectedName) && Boolean(selectedDomain);
      }
      if (step2Sub === 'name') return Boolean(selectedName);
      if (step2Sub === 'own-name') return Boolean(effectiveName);
      return Boolean(selectedDomain); // domain / own-domain → domain chosen
    }
    if (step === 3) return Boolean(selectedLogo);
    return true;
  }, [
    step,
    isMobile,
    step2Sub,
    manualMode,
    country,
    selectedName,
    selectedDomain,
    effectiveName,
    connectStage,
    connectDomainValid,
    connectLoading,
    entriSuccess,
    selectedLogo,
  ]);

  const dismissIntro = useCallback(() => setDisplayIntro(false), []);

  const value = useMemo<CustomizerContextValue>(
    () => ({
      status,
      isMobile,
      countries: regions.countries,
      countryGroups: regions.groups,
      country,
      currency,
      currencySymbol: currencySymbol(currency),
      setCountry,
      step,
      stepKey,
      totalSteps: WIZARD_STEPS.length,
      steps: WIZARD_STEPS,
      mainNextLabel,
      mainNextPlaceholders,
      goNext,
      goBack,
      canAdvance,
      displayIntro,
      dismissIntro,
      step2Sub,
      names,
      loadingNames,
      domains,
      selectedName,
      selectedDomain,
      setName,
      setDomain,
      refreshNames,
      refreshDomains,
      domainsPaging,
      domainsNav,
      manualMode,
      setManualMode,
      typedName,
      setTypedName,
      manualDomains,
      loadingManualDomains,
      connectStage,
      openConnectDomain,
      connectDomain,
      setConnectDomain,
      connectDomainValid,
      submitConnectDomain,
      editConnectDomain,
      connectLoading,
      connectError,
      dnsRecords,
      connectProvider,
      connectAutoSupported,
      openEntri,
      entriOpen,
      aiLogo,
      setAiLogo,
      logos,
      loadingLogos,
      logosCount,
      selectedLogo,
      selectLogo,
      refreshLogos,
      uploadedFile,
      onSelectLogoFile,
      logoProcessing,
      logoError,
      resetUploadedLogo,
      previewUrl,
      previewTokens,
      brandName,
      deploy,
      deployPhase,
      startDeploy,
    }),
    [
      status,
      isMobile,
      regions,
      country,
      currency,
      setCountry,
      step,
      stepKey,
      mainNextLabel,
      mainNextPlaceholders,
      goNext,
      goBack,
      canAdvance,
      displayIntro,
      dismissIntro,
      step2Sub,
      names,
      loadingNames,
      domains,
      selectedName,
      selectedDomain,
      setName,
      setDomain,
      refreshNames,
      refreshDomains,
      domainsPaging,
      domainsNav,
      manualMode,
      setManualMode,
      typedName,
      setTypedName,
      manualDomains,
      loadingManualDomains,
      connectStage,
      openConnectDomain,
      connectDomain,
      setConnectDomain,
      connectDomainValid,
      submitConnectDomain,
      editConnectDomain,
      connectLoading,
      connectError,
      dnsRecords,
      connectProvider,
      connectAutoSupported,
      openEntri,
      entriOpen,
      aiLogo,
      setAiLogo,
      logos,
      loadingLogos,
      logosCount,
      selectedLogo,
      selectLogo,
      refreshLogos,
      uploadedFile,
      onSelectLogoFile,
      logoProcessing,
      logoError,
      resetUploadedLogo,
      previewUrl,
      previewTokens,
      brandName,
      deploy,
      deployPhase,
      startDeploy,
    ],
  );

  return <CustomizerContext.Provider value={value}>{children}</CustomizerContext.Provider>;
}

export function useCustomizer(): CustomizerContextValue {
  const ctx = useContext(CustomizerContext);
  if (!ctx) throw new Error('useCustomizer must be used within a CustomizerProvider');
  return ctx;
}